/**
 * MilestoneSummarySection — a calm "what's happened" timeline.
 *
 * Customer-safe rows from the projection: each row has {kind, at, label}.
 * Customer rows have the LABEL already softened (e.g. "Outreach sent",
 * not "Outreach sent to vendor abc12345"). The projection layer enforces
 * that; we render verbatim.
 *
 * We show the latest 5 items in reverse-chronological order with a thin
 * left timeline rail.
 */

import React from 'react';

const KIND_DOT = {
  intake:       'bg-gray-300',
  brief_sealed: 'bg-violet-400',
  outreach:     'bg-blue-400',
  booking:      'bg-emerald-400',
  event_day:    'bg-amber-400',
  completed:    'bg-emerald-600',
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_) {
    return null;
  }
}

export default function MilestoneSummarySection({ milestones }) {
  if (!Array.isArray(milestones) || milestones.length === 0) return null;
  // Reverse-chrono so newest is at top.
  const items = [...milestones].reverse().slice(0, 5);
  return (
    <div data-testid="ops-milestones">
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
        Timeline
      </p>
      <ol className="space-y-2.5 border-l border-gray-100 pl-3">
        {items.map((m, idx) => {
          const dot = KIND_DOT[m.kind] || 'bg-gray-300';
          const when = fmtDate(m.at);
          return (
            <li
              key={`${m.kind || 'm'}-${idx}`}
              className="relative text-xs"
              data-testid={`ops-milestone-${m.kind || 'unknown'}-${idx}`}
            >
              <span
                className={`absolute -left-[15px] top-1 w-2 h-2 rounded-full ${dot}`}
                aria-hidden
              />
              <span className="text-gray-700">{m.label}</span>
              {when && <span className="text-gray-400"> · {when}</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
